import {
  useState,
  useReducer,
  Suspense,
  useCallback,
  memo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { lazy } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { UserProfileProvider, useUserProfile } from "./components/UserProfileContext";
import { BottomNavigation } from "./components/BottomNavigation";
import { ThemeProvider } from "./components/ThemeContext";
import { LoadingStateProvider } from "./components/LoadingStateContext";
import {
  ErrorBoundary,
  PageErrorBoundary,
  CriticalErrorBoundary,
} from "./components/ErrorBoundary";
import { NetworkErrorHandler } from "./components/NetworkErrorHandler";
import { PageLoading } from "./components/ui/loading";
import { CopyFeedbackProvider } from "./components/CopyFeedbackProvider";
import { toast } from "sonner";
import { saveAuth, loadAuth, clearAuth, getAuthContext } from "./utils/auth";
import { clearBiometricCredential } from "./utils/biometric-storage";
import { apiClient } from "./utils/apiClient";
import { authService } from "./services/auth";
import { resolveRegionForSignup } from "./utils/regions";
import { apiBaseUrl } from "./utils/config";
import type { CatalogOrderPayload } from "./src/types/catalogOrder";

const rawGoogleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
const GOOGLE_AUTH_ENABLED = Boolean(rawGoogleClientId);
const GOOGLE_PROVIDER_CLIENT_ID =
  rawGoogleClientId || "placeholder.apps.googleusercontent.com";
// Simple hash-based deep-linking utilities
function encodeHash(tab: string, data?: any): string {
  try {
    const p = new URLSearchParams();
    p.set("tab", tab);
    if (data) {
      if (data.groupId) p.set("groupId", String(data.groupId));
      if (data.billSplitId) p.set("billSplitId", String(data.billSplitId));
      if (data.billId) p.set("billId", String(data.billId));
      if (data.transactionId) p.set("transactionId", String(data.transactionId));
      if (data.friendId) p.set("friendId", String(data.friendId));
      if (data.from) p.set("from", String(data.from));
      if (data.vendorId) p.set("vendorId", String(data.vendorId));
      if (data.vendorName) p.set("vendorName", String(data.vendorName));
      if (data.vendorDescription)
        p.set("vendorDescription", String(data.vendorDescription));
      const nestedPaymentRequest = (data as any).paymentRequest || null;
      const paymentRequestId =
        (nestedPaymentRequest && nestedPaymentRequest.requestId) ||
        data.paymentRequestId ||
        data.requestId;
      if (paymentRequestId) {
        p.set("paymentRequestId", String(paymentRequestId));
      }
      const directRequestId =
        (nestedPaymentRequest && nestedPaymentRequest.directRequestId) ||
        data.directRequestId;
      if (directRequestId) {
        p.set("directRequestId", String(directRequestId));
      }
    }
    const s = p.toString();
    return s ? `#${s}` : "";
  } catch {
    return `#tab=${encodeURIComponent(tab)}`;
  }
}

function parseHash(): { tab?: string; data: any } {
  try {
    const h = (window.location.hash || "").replace(/^#/, "");
    const p = new URLSearchParams(h);
    const tab = p.get("tab") || undefined;
    const data: any = {};
    const groupId = p.get("groupId");
    const billSplitId = p.get("billSplitId");
    const billId = p.get("billId");
    const transactionId = p.get("transactionId");
    const friendId = p.get("friendId");
    const from = p.get("from");
    const vendorId = p.get("vendorId");
    const vendorName = p.get("vendorName");
    const vendorDescription = p.get("vendorDescription");
    const paymentRequestId = p.get("paymentRequestId");
    const directRequestId = p.get("directRequestId");
    if (groupId) data.groupId = groupId;
    if (billSplitId) data.billSplitId = billSplitId;
    if (billId) data.billId = billId;
    if (transactionId) data.transactionId = transactionId;
    if (friendId) data.friendId = friendId;
    if (from) data.from = from;
    if (vendorId) data.vendorId = vendorId;
    if (vendorName) data.vendorName = vendorName;
    if (vendorDescription) data.vendorDescription = vendorDescription;
    if (paymentRequestId) data.paymentRequestId = paymentRequestId;
    if (directRequestId) data.directRequestId = directRequestId;
    return { tab, data };
  } catch {
    return { data: {} };
  }
}

// Lazily load heavy, secondary screens to keep initial load quick
import { HomeScreen } from "./components/HomeScreen";
import { LoginScreen } from "./components/LoginScreen";
import { RegisterScreen } from "./components/RegisterScreen";
import { OTPVerificationScreen } from "./components/OTPVerificationScreen";
const SendMoney = lazy(() =>
  import("./components/SendMoney").then((m) => ({ default: m.SendMoney })),
);
const SplitBill = lazy(() =>
  import("./components/SplitBill").then((m) => ({ default: m.SplitBill })),
);
const FriendsList = lazy(() =>
  import("./components/FriendsList").then((m) => ({ default: m.FriendsList })),
);
const BillsScreen = lazy(() =>
  import("./components/BillsScreen").then((m) => ({ default: m.BillsScreen })),
);
const ProfileScreen = lazy(() =>
  import("./components/ProfileScreen").then((m) => ({
    default: m.ProfileScreen,
  })),
);
const RequestMoney = lazy(() =>
  import("./components/RequestMoney").then((m) => ({
    default: m.RequestMoney,
  })),
);
const SettingsScreen = lazy(() =>
  import("./components/SettingsScreen").then((m) => ({
    default: m.SettingsScreen,
  })),
);
const NotificationsScreen = lazy(() =>
  import("./components/NotificationsScreen").then((m) => ({
    default: m.NotificationsScreen,
  })),
);
const KycVerificationScreen = lazy(() =>
  import("./components/KycVerificationScreen").then((m) => ({
    default: m.KycVerificationScreen,
  })),
);
const CatalogScreen = lazy(() =>
  import("./src/screens/CatalogScreen").then((m) => ({
    default: m.CatalogScreen,
  })),
);

const deriveSplitPrefillFromCatalogPayload = (data?: any) => {
  if (!data) return null;
  const fallbackCatalogOrder: CatalogOrderPayload | null =
    (data.catalogOrder as CatalogOrderPayload | undefined) ||
    (data.splitPrefill?.catalogOrder as CatalogOrderPayload | undefined) ||
    null;

  if (data.splitPrefill) {
    return {
      ...data.splitPrefill,
      catalogOrder: data.splitPrefill.catalogOrder ?? fallbackCatalogOrder,
      deliveryOption:
        data.splitPrefill.deliveryOption ||
        (data.deliveryOption
          ? {
            type: data.deliveryOption.type,
            location: data.deliveryOption.location,
          }
          : undefined),
    };
  }

  const amountSource =
    typeof data.totalAmount === "number"
      ? data.totalAmount
      : Number(
        (data.requestData?.amount as string | number | undefined) ??
        data.sendMoneyPrefill?.prefillAmount ??
        null,
      );
  const totalAmount = Number.isFinite(amountSource) ? amountSource : null;

  return {
    billName: data.vendorName
      ? `${data.vendorName} order`
      : undefined,
    totalAmount: totalAmount ?? undefined,
    description:
      data.description ||
      data.requestData?.message ||
      data.sendMoneyPrefill?.description ||
      undefined,
    catalogOrder: fallbackCatalogOrder ?? undefined,
    deliveryOption: data.deliveryOption
      ? {
        type: data.deliveryOption.type,
        location: data.deliveryOption.location,
      }
      : undefined,
  };
};

const cloneCatalogOrderPayload = (
  payload?: CatalogOrderPayload | null,
): CatalogOrderPayload | null => {
  if (!payload) return null;
  return {
    ...payload,
    items: Array.isArray(payload.items)
      ? payload.items.map((item) => ({ ...item }))
      : [],
    deliveryOption: payload.deliveryOption
      ? { ...payload.deliveryOption }
      : undefined,
  };
};

// Lazy load secondary screens
const AccountSettingsScreen = lazy(() =>
  import("./components/AccountSettingsScreen").then((m) => ({
    default: m.AccountSettingsScreen,
  })),
);
const BusinessSettingsScreen = lazy(() =>
  import("./components/BusinessSettingsScreen").then((m) => ({
    default: m.BusinessSettingsScreen,
  })),
);
const PaymentMethodsScreen = lazy(() =>
  import("./components/PaymentMethodsScreen").then((m) => ({
    default: m.PaymentMethodsScreen,
  })),
);
const SecurityScreen = lazy(() =>
  import("./components/SecurityScreen").then((m) => ({
    default: m.SecurityScreen,
  })),
);
const TransactionDetailsScreen = lazy(() =>
  import("./components/TransactionDetailsScreen").then((m) => ({
    default: m.TransactionDetailsScreen,
  })),
);
const BillSplitDetailsScreen = lazy(() =>
  import("./components/BillSplitDetailsScreen").then((m) => ({
    default: m.BillSplitDetailsScreen,
  })),
);
const EditBillSplitScreen = lazy(() =>
  import("./components/EditBillSplitScreen").then((m) => ({
    default: m.EditBillSplitScreen,
  })),
);
const BillPaymentScreen = lazy(() =>
  import("./components/BillPaymentScreen").then((m) => ({
    default: m.BillPaymentScreen,
  })),
);
const UpcomingPaymentsScreen = lazy(() =>
  import("./components/UpcomingPaymentsScreen").then((m) => ({
    default: m.UpcomingPaymentsScreen,
  })),
);
const PaymentFlowScreen = lazy(() =>
  import("./components/PaymentFlowScreen").then((m) => ({
    default: m.PaymentFlowScreen,
  })),
);
const TransactionHistoryScreen = lazy(() =>
  import("./components/TransactionHistoryScreen").then((m) => ({
    default: m.TransactionHistoryScreen,
  })),
);
const OrdersScreen = lazy(() =>
  import("./components/OrdersScreen").then((m) => ({
    default: m.OrdersScreen,
  })),
);
const SpendingInsightsScreen = lazy(() =>
  import("./components/SpendingInsightsScreen").then((m) => ({
    default: m.SpendingInsightsScreen,
  })),
);
const PaymentRequestCancelScreen = lazy(() =>
  import("./components/PaymentRequestCancelScreen").then((m) => ({
    default: m.PaymentRequestCancelScreen,
  })),
);

// Lazy load group-related screens
const CreateGroupScreen = lazy(() =>
  import("./components/CreateGroupScreen").then((m) => ({
    default: m.CreateGroupScreen,
  })),
);
const GroupDetailsScreen = lazy(() =>
  import("./components/GroupDetailsScreen").then((m) => ({
    default: m.GroupDetailsScreen,
  })),
);
const GroupAccountScreen = lazy(() =>
  import("./components/GroupAccountScreen").then((m) => ({
    default: m.GroupAccountScreen,
  })),
);
const GroupMembersScreen = lazy(() =>
  import("./components/GroupMembersScreen").then((m) => ({
    default: m.GroupMembersScreen,
  })),
);
const AddGroupMemberScreen = lazy(() =>
  import("./components/AddGroupMemberScreen").then((m) => ({
    default: m.AddGroupMemberScreen,
  })),
);
const MemberInviteScreen = lazy(() =>
  import("./components/MemberInviteScreen").then((m) => ({
    default: m.MemberInviteScreen,
  })),
);

// Lazy load friend-related screens
const SendReminderScreen = lazy(() =>
  import("./components/SendReminderScreen").then((m) => ({
    default: m.SendReminderScreen,
  })),
);
const FriendProfileScreen = lazy(() =>
  import("./components/FriendProfileScreen").then((m) => ({
    default: m.FriendProfileScreen,
  })),
);
const AddFriendScreen = lazy(() =>
  import("./components/AddFriendScreen").then((m) => ({
    default: m.AddFriendScreen,
  })),
);
const AddVendorScreen = lazy(() =>
  import("./components/AddVendorScreen").then((m) => ({
    default: m.AddVendorScreen,
  })),
);
const ContactSyncScreen = lazy(() =>
  import("./components/ContactSyncScreen").then((m) => ({
    default: m.ContactSyncScreen,
  })),
);
const ViewAllFriendsScreen = lazy(() =>
  import("./components/ViewAllFriendsScreen").then((m) => ({
    default: m.ViewAllFriendsScreen,
  })),
);

// Lazy load remaining screens
const RecurringPaymentsScreen = lazy(() =>
  import("./components/RecurringPaymentsScreen").then((m) => ({
    default: m.RecurringPaymentsScreen,
  })),
);
const SetupRecurringPaymentScreen = lazy(() =>
  import("./components/SetupRecurringPaymentScreen").then((m) => ({
    default: m.SetupRecurringPaymentScreen,
  })),
);
const VirtualAccountScreen = lazy(() =>
  import("./components/VirtualAccountScreen").then((m) => ({
    default: m.VirtualAccountScreen,
  })),
);
const BankingRedirectScreen = lazy(() =>
  import("./components/BankingRedirectScreen").then((m) => ({
    default: m.BankingRedirectScreen,
  })),
);
const PaymentConfirmationScreen = lazy(() =>
  import("./components/PaymentConfirmationScreen").then((m) => ({
    default: m.PaymentConfirmationScreen,
  })),
);
// Navigation state management using useReducer
interface NavigationState {
  activeTab: string;
  selectedTransactionId: string | null;
  selectedBillSplitId: string | null;
  editBillSplitId: string | null;
  payBillId: string | null;
  paymentRequest: any;
  paymentRequestRef: {
    requestId?: string | null;
    directRequestId?: string | null;
  } | null;
  selectedFriendId: string | null;
  currentGroupId: string | null;
  vendorId: string | null;
  groupNavigationContext: string | null;
  recurringPaymentId: string | null;
  recurringPaymentEditMode: boolean;
  bankingRedirectData: any;
  paymentConfirmationData: any;
  requestMoneyData: any;
  sendMoneyData: any;
  catalogOrderData: CatalogOrderPayload | null;
  vendorShopData: {
    vendorId?: string;
    vendorName?: string;
    vendorDescription?: string;
  } | null;
  orderDeliveryOption: { type: "takeout" | "delivery"; location?: string } | null;
  splitPrefillData: {
    billName?: string;
    totalAmount?: number;
    description?: string;
  } | null;
  reminderData: any;
  historyBackTo: string | null;
  historyBackPayload: any;
  paymentMethodsIntent: { startAddFlow?: boolean } | null;
  cancelRequestId: string | null;
  splitFocusSection: string | null;
  friendPickerSelection: string[];
  friendPickerSourceTab: string | null;
  friendPickerSplitPrefill:
  | {
    billName?: string;
    totalAmount?: number;
    description?: string;
  }
  | null;
  friendPickerDeliveryOption: { type: "takeout" | "delivery"; location?: string } | null;
  pendingSplitFriendIds: string[];
  // Bump to force SplitBill remount and clear lingering inputs
  splitKey: number;
  // Optional friend to prefill in SplitBill
  splitPrefillFriendId: string | null;
}

type NavigationAction =
  | { type: "SET_TAB"; payload: string }
  | { type: "SET_TRANSACTION_ID"; payload: string | null }
  | { type: "SET_BILL_SPLIT_ID"; payload: string | null }
  | { type: "SET_EDIT_BILL_SPLIT_ID"; payload: string | null }
  | { type: "SET_PAY_BILL_ID"; payload: string | null }
  | { type: "SET_PAYMENT_REQUEST"; payload: any }
  | {
    type: "SET_PAYMENT_REQUEST_REF";
    payload:
    | {
      requestId?: string | null;
      directRequestId?: string | null;
    }
    | null;
  }
  | { type: "SET_FRIEND_ID"; payload: string | null }
  | { type: "SET_GROUP_ID"; payload: string | null }
  | { type: "SET_VENDOR_ID"; payload: string | null }
  | { type: "SET_GROUP_CONTEXT"; payload: string | null }
  | {
    type: "SET_RECURRING_PAYMENT";
    payload: { id: string | null; editMode: boolean };
  }
  | { type: "SET_BANKING_REDIRECT"; payload: any }
  | { type: "SET_PAYMENT_CONFIRMATION"; payload: any }
  | { type: "SET_REQUEST_DATA"; payload: any }
  | { type: "SET_SEND_DATA"; payload: any }
  | { type: "SET_CATALOG_ORDER_DATA"; payload: CatalogOrderPayload | null }
  | {
    type: "SET_VENDOR_SHOP";
    payload:
    | {
      vendorId?: string;
      vendorName?: string;
      vendorDescription?: string;
    }
    | null;
  }
  | {
    type: "SET_ORDER_DELIVERY_OPTION";
    payload: { type: "takeout" | "delivery"; location?: string } | null;
  }
  | {
    type: "SET_SPLIT_PREFILL_DATA";
    payload: {
      billName?: string;
      totalAmount?: number;
      description?: string;
    } | null;
  }
  | { type: "SET_REMINDER_DATA"; payload: any }
  | {
    type: "SET_HISTORY_BACK_TO";
    payload:
    | string
    | null
    | {
      tab: string | null;
      data?: any;
    };
  }
  | {
    type: "SET_PAYMENT_METHODS_INTENT";
    payload: { startAddFlow?: boolean } | null;
  }
  | { type: "CLEAR_ALL" }
  | { type: "CLEAR_PREVIOUS_STATE"; payload: string }
  | { type: "BUMP_SPLIT_KEY" }
  // prettier-ignore
  | { type: 'SET_SPLIT_PREFILL_FRIEND_ID'; payload: string | null }
  | { type: 'SET_CANCEL_REQUEST_ID'; payload: string | null }
  | { type: 'SET_SPLIT_FOCUS'; payload: string | null }
  | {
    type: "SET_FRIEND_PICKER_SPLIT_PREFILL";
    payload:
    | {
      billName?: string;
      totalAmount?: number;
      description?: string;
    }
    | null;
  }
  | {
    type: "SET_FRIEND_PICKER_DELIVERY_OPTION";
    payload:
    | {
      type: "takeout" | "delivery";
      location?: string;
    }
    | null;
  }
  | { type: 'SET_FRIEND_PICKER_STATE'; payload: { selectedIds: string[]; sourceTab: string | null } }
  | { type: 'SET_PENDING_SPLIT_FRIENDS'; payload: string[] };

const initialState: NavigationState = {
  activeTab: "home",
  selectedTransactionId: null,
  selectedBillSplitId: null,
  editBillSplitId: null,
  payBillId: null,
  paymentRequest: null,
  paymentRequestRef: null,
  selectedFriendId: null,
  currentGroupId: null,
  vendorId: null,
  groupNavigationContext: null,
  recurringPaymentId: null,
  recurringPaymentEditMode: false,
  bankingRedirectData: null,
  paymentConfirmationData: null,
  splitKey: 0,
  splitPrefillFriendId: null,
  requestMoneyData: null,
  sendMoneyData: null,
  catalogOrderData: null,
  vendorShopData: null,
  orderDeliveryOption: null,
  splitPrefillData: null,
  reminderData: null,
  historyBackTo: null,
  historyBackPayload: null,
  paymentMethodsIntent: null,
  cancelRequestId: null,
  splitFocusSection: null,
  friendPickerSelection: [],
  friendPickerSourceTab: null,
  friendPickerSplitPrefill: null,
  friendPickerDeliveryOption: null,
  pendingSplitFriendIds: [],
};

const PRIMARY_TABS = new Set(["home", "friends", "split", "bills", "profile"]);

const navigationReducer = (
  state: NavigationState,
  action: NavigationAction,
): NavigationState => {
  switch (action.type) {
    case "SET_TAB":
      return { ...state, activeTab: action.payload };
    case "BUMP_SPLIT_KEY":
      return { ...state, splitKey: state.splitKey + 1 };
    case "SET_TRANSACTION_ID":
      return { ...state, selectedTransactionId: action.payload };
    case "SET_BILL_SPLIT_ID":
      return { ...state, selectedBillSplitId: action.payload };
    case "SET_EDIT_BILL_SPLIT_ID":
      return { ...state, editBillSplitId: action.payload };
    case "SET_PAY_BILL_ID":
      return { ...state, payBillId: action.payload };
    case "SET_PAYMENT_REQUEST":
      return { ...state, paymentRequest: action.payload };
    case "SET_PAYMENT_REQUEST_REF":
      return { ...state, paymentRequestRef: action.payload };
    case "SET_FRIEND_ID":
      return { ...state, selectedFriendId: action.payload };
    case "SET_GROUP_ID":
      return { ...state, currentGroupId: action.payload };
    case "SET_VENDOR_ID":
      return { ...state, vendorId: action.payload };
    case "SET_GROUP_CONTEXT":
      return { ...state, groupNavigationContext: action.payload };
    case "SET_SPLIT_PREFILL_FRIEND_ID":
      return { ...state, splitPrefillFriendId: action.payload };
    case "SET_RECURRING_PAYMENT":
      return {
        ...state,
        recurringPaymentId: action.payload.id,
        recurringPaymentEditMode: action.payload.editMode,
      };
    case "SET_BANKING_REDIRECT":
      return { ...state, bankingRedirectData: action.payload };
    case "SET_PAYMENT_CONFIRMATION":
      return { ...state, paymentConfirmationData: action.payload };
    case "SET_REQUEST_DATA":
      return { ...state, requestMoneyData: action.payload };
    case "SET_SEND_DATA":
      return { ...state, sendMoneyData: action.payload };
    case "SET_CATALOG_ORDER_DATA":
      return { ...state, catalogOrderData: action.payload };
    case "SET_VENDOR_SHOP":
      return { ...state, vendorShopData: action.payload };
    case "SET_ORDER_DELIVERY_OPTION":
      return { ...state, orderDeliveryOption: action.payload };
    case "SET_SPLIT_PREFILL_DATA":
      return { ...state, splitPrefillData: action.payload };
    case "SET_REMINDER_DATA":
      return { ...state, reminderData: action.payload };
    case "SET_HISTORY_BACK_TO":
      if (
        action.payload !== null &&
        typeof action.payload === "object" &&
        "tab" in action.payload
      ) {
        return {
          ...state,
          historyBackTo: action.payload.tab ?? null,
          historyBackPayload: action.payload.data ?? null,
        };
      }
      return {
        ...state,
        historyBackTo: action.payload,
        historyBackPayload: null,
      };
    case "SET_PAYMENT_METHODS_INTENT":
      return { ...state, paymentMethodsIntent: action.payload };
    case "SET_CANCEL_REQUEST_ID":
      return { ...state, cancelRequestId: action.payload };
    case "SET_SPLIT_FOCUS":
      return { ...state, splitFocusSection: action.payload };
    case "SET_FRIEND_PICKER_STATE":
      return {
        ...state,
        friendPickerSelection: action.payload.selectedIds,
        friendPickerSourceTab: action.payload.sourceTab,
      };
    case "SET_FRIEND_PICKER_DELIVERY_OPTION":
      return { ...state, friendPickerDeliveryOption: action.payload };
    case "SET_FRIEND_PICKER_SPLIT_PREFILL":
      return { ...state, friendPickerSplitPrefill: action.payload };
    case "SET_PENDING_SPLIT_FRIENDS":
      return { ...state, pendingSplitFriendIds: action.payload };
    case "CLEAR_ALL":
      return initialState;
    case "CLEAR_PREVIOUS_STATE": {
      // Clear state based on previous tab
      const newState = { ...state };
      const previousTab = action.payload;

      switch (previousTab) {
        case "transaction-history":
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          break;
        case "payment-methods":
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          newState.paymentMethodsIntent = null;
          break;
        case "add-vendor":
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          break;
        case "orders":
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          break;
        case "transaction-details":
          newState.selectedTransactionId = null;
          break;
        case "bill-split-details":
          newState.selectedBillSplitId = null;
          newState.catalogOrderData = null;
          break;
        case "edit-bill-split":
          newState.editBillSplitId = null;
          break;
        case "pay-bill":
          newState.payBillId = null;
          break;
        case "payment-flow":
          newState.paymentRequest = null;
          newState.paymentRequestRef = null;
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          newState.catalogOrderData = null;
          break;
        case "friend-profile":
          newState.selectedFriendId = null;
          break;
        case "request":
          newState.requestMoneyData = null;
          break;
        case "send":
          newState.sendMoneyData = null;
          newState.catalogOrderData = null;
          break;
        case "vendor-shop":
          newState.vendorShopData = null;
          newState.orderDeliveryOption = null;
          newState.vendorId = null;
          break;
        case "send-reminder":
          newState.reminderData = null;
          break;
        case "payment-request-cancel":
          newState.cancelRequestId = null;
          break;
        case "recurring-payments":
        case "setup-recurring-payment":
          newState.recurringPaymentId = null;
          newState.recurringPaymentEditMode = false;
          break;
        case "banking-redirect":
          newState.bankingRedirectData = null;
          break;
        case "payment-confirmation":
          newState.paymentConfirmationData = null;
          break;
        case "spending-insights":
          newState.historyBackTo = null;
          newState.historyBackPayload = null;
          break;
        case "split":
          newState.splitPrefillFriendId = null;
          newState.pendingSplitFriendIds = [];
          newState.splitFocusSection = null;
          newState.orderDeliveryOption = null;
          newState.splitPrefillData = null;
          newState.catalogOrderData = null;
          newState.vendorId = null;
          break;
        case "view-all-friends":
          newState.friendPickerSelection = [];
          newState.friendPickerSourceTab = null;
          newState.friendPickerSplitPrefill = null;
          newState.friendPickerDeliveryOption = null;
          newState.catalogOrderData = null;
          break;
      }
      return newState;
    }
    default:
      return state;
  }
};

// Memoized loading component
const MemoizedPageLoading = memo(PageLoading);

// Performance monitoring hook (logs disabled by default to reduce console noise)
const usePerformanceMonitoring = () => {
  useEffect(() => {
    // Performance observer for monitoring navigation timing
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          void entry;
          // Intentionally no logging; enable here if needed
        }
      });

      observer.observe({ entryTypes: ["navigation", "measure"] });

      return () => observer.disconnect();
    }
  }, []);
};

function AppContent() {
  const [navState, dispatch] = useReducer(navigationReducer, initialState);
  const [mountedPrimaryTabs, setMountedPrimaryTabs] = useState<string[]>(() =>
    PRIMARY_TABS.has(initialState.activeTab) ? [initialState.activeTab] : [],
  );
  const tabCacheRef = useRef<Map<string, ReactNode>>(new Map());
  const lastPrimaryTabRef = useRef<string>(initialState.activeTab);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const [showLogin, setShowLogin] = useState(true);
  const [registerPrefill, setRegisterPrefill] = useState<{
    phone?: string;
    country?: string;
  } | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingOTP, setPendingOTP] = useState<null | {
    phone: string;
    region: string;
    isNewUser: boolean;
    name?: string;
    demoOTP?: string;
  }>(null);
  const DEBUG_NAV = false;
  const { refreshUserProfile, userProfile } = useUserProfile();

  // Performance monitoring
  usePerformanceMonitoring();

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Network status for connection-aware features (not currently used)

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const storedContext = getAuthContext();
      if (storedContext && storedContext.apiBaseUrl !== apiBaseUrl) {
        clearAuth();
        clearBiometricCredential();
      }
      try {
        // Check for stored authentication token/session
        const stored = loadAuth();

        if (stored) {
          // Validate stored session (in real app, this would make an API call)
          const { auth, user } = stored;

          // Simple validation - in production, verify token with backend
          if (
            auth.token &&
            typeof auth.expiresAt === "number" &&
            auth.expiresAt > Date.now()
          ) {
            setIsAuthenticated(true);
            console.log("Restored user session:", user.name);
          } else {
            // Clear invalid session
            clearAuth();
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // Clear potentially corrupted data
        clearAuth();
      } finally {
        setIsInitializing(false);
      }
    };

    checkAuthStatus();
  }, []);





  // Keep primary tabs mounted to avoid refetch/reload on quick navigation
  useEffect(() => {
    if (!isAuthenticated) {
      setMountedPrimaryTabs(
        PRIMARY_TABS.has(initialState.activeTab)
          ? [initialState.activeTab]
          : [],
      );
      lastPrimaryTabRef.current = initialState.activeTab;
      tabCacheRef.current.clear();
      return;
    }

    if (PRIMARY_TABS.has(navState.activeTab)) {
      lastPrimaryTabRef.current = navState.activeTab;
      setMountedPrimaryTabs((prev) =>
        prev.includes(navState.activeTab)
          ? prev
          : [...prev, navState.activeTab],
      );
    }
  }, [isAuthenticated, navState.activeTab]);

  // Memoized navigation handler
  const handleNavigate = useCallback(
    (tab: string, data?: any) => {
      try {
        performance.mark("navigation-start");

        if (DEBUG_NAV) console.log("Navigation:", tab, data);

        if (PRIMARY_TABS.has(tab)) {
          tabCacheRef.current.delete(tab);
        }

        const previousTab = navState.activeTab;
        const previousPaymentRequest = navState.paymentRequest;
        const previousPaymentRequestRef = navState.paymentRequestRef;

        const inferBackPayload = () => {
          switch (previousTab) {
            case "bill-split-details":
              return navState.selectedBillSplitId
                ? { billSplitId: navState.selectedBillSplitId }
                : null;
            case "banking-redirect":
              return navState.bankingRedirectData
                ? { ...navState.bankingRedirectData }
                : null;
            case "payment-confirmation":
              return navState.paymentConfirmationData
                ? { ...navState.paymentConfirmationData }
                : null;
            case "vendor-shop":
              return navState.vendorShopData
                ? { ...navState.vendorShopData }
                : null;
            default:
              return null;
          }
        };

        const previousBackPayload = inferBackPayload();

        // Clear previous state
        dispatch({ type: "CLEAR_PREVIOUS_STATE", payload: previousTab });

        // Handle navigation with data
        const catalogOrderFromPayload = data
          ? (data.catalogOrder as CatalogOrderPayload | undefined) ||
            (data.sendMoneyPrefill as any)?.catalogOrder ||
            (data.splitPrefill as any)?.catalogOrder ||
            null
          : null;
        if (catalogOrderFromPayload) {
          dispatch({
            type: "SET_CATALOG_ORDER_DATA",
            payload: cloneCatalogOrderPayload(catalogOrderFromPayload),
          });
        } else if (navState.catalogOrderData) {
          dispatch({ type: "SET_CATALOG_ORDER_DATA", payload: null });
        }

        if (data) {
          if (data.vendorId !== undefined) {
            dispatch({
              type: "SET_VENDOR_ID",
              payload: data.vendorId ?? null,
            });
          }
          switch (tab) {
            case "transaction-history":
              dispatch({
                type: "SET_HISTORY_BACK_TO",
                payload: data.from || "home",
              });
              break;
            case "transaction-details":
              if (data.transactionId) {
                dispatch({
                  type: "SET_TRANSACTION_ID",
                  payload: data.transactionId,
                });
              }
              break;
            case "bill-split-details":
              if (data.billSplitId) {
                dispatch({
                  type: "SET_BILL_SPLIT_ID",
                  payload: data.billSplitId,
                });
              }
              break;
            case "edit-bill-split":
              if (data.billSplitId) {
                dispatch({
                  type: "SET_EDIT_BILL_SPLIT_ID",
                  payload: data.billSplitId,
                });
              }
              break;
            case "pay-bill":
              if (data.billId) {
                dispatch({ type: "SET_PAY_BILL_ID", payload: data.billId });
              }
              break;
            case "payment-flow": {
              const requestPayload = data.paymentRequest;
              const requestId =
                requestPayload?.requestId ||
                data.paymentRequestId ||
                data.requestId ||
                null;
              const directRequestId =
                requestPayload?.directRequestId ||
                data.directRequestId ||
                null;
              const hasReference = Boolean(requestId || directRequestId);

              if (requestPayload && catalogOrderFromPayload) {
                (requestPayload as any).catalogOrderMetadata = {
                  ...catalogOrderFromPayload,
                };
              }

              const shouldRestorePrevious =
                !requestPayload &&
                !hasReference &&
                previousTab === "payment-flow" &&
                Boolean(previousPaymentRequest);

              if (shouldRestorePrevious) {
                dispatch({
                  type: "SET_PAYMENT_REQUEST",
                  payload: previousPaymentRequest,
                });
                dispatch({
                  type: "SET_PAYMENT_REQUEST_REF",
                  payload: previousPaymentRequestRef,
                });
                break;
              }

              dispatch({
                type: "SET_PAYMENT_REQUEST_REF",
                payload: hasReference
                  ? { requestId, directRequestId }
                  : null,
              });

              if (requestPayload?.billSplitId) {
                dispatch({
                  type: "SET_PAY_BILL_ID",
                  payload: requestPayload.billSplitId,
                });
                dispatch({ type: "SET_TAB", payload: "pay-bill" });
                return;
              }

              if (requestPayload) {
                dispatch({
                  type: "SET_PAYMENT_REQUEST",
                  payload: requestPayload,
                });
              } else if (hasReference) {
                dispatch({ type: "SET_PAYMENT_REQUEST", payload: null });
              }

              const explicitBackTarget =
                typeof (data as any)?.from === "string"
                  ? (data as any).from
                  : null;
              const fallbackBackTarget =
                explicitBackTarget ||
                (previousTab && previousTab !== "payment-flow"
                  ? previousTab
                  : null) ||
                "home";
              const backPayload =
                (data as any)?.backPayload ??
                previousBackPayload ??
                null;

              dispatch({
                type: "SET_HISTORY_BACK_TO",
                payload: { tab: fallbackBackTarget, data: backPayload },
              });
              break;
            }
            case "friend-profile":
              if (data.friendId) {
                dispatch({ type: "SET_FRIEND_ID", payload: data.friendId });
              }
              break;
            case "group-details":
            case "group-account":
            case "bills":
            case "split":
            case "virtual-account":
              if (data.groupId) {
                dispatch({ type: "SET_GROUP_ID", payload: data.groupId });
                dispatch({ type: "SET_GROUP_CONTEXT", payload: tab });
              }
        if (tab === "split") {
          const normalizedSplitPrefill = deriveSplitPrefillFromCatalogPayload(
            data,
          );
          const deliveryOptionFromPayload =
            (data as any)?.deliveryOption ||
            normalizedSplitPrefill?.deliveryOption ||
            null;
          dispatch({
            type: "SET_ORDER_DELIVERY_OPTION",
            payload: deliveryOptionFromPayload
              ? {
                type: deliveryOptionFromPayload.type,
                location: deliveryOptionFromPayload.location,
              }
              : null,
          });
          const fallbackPrefill =
            normalizedSplitPrefill ??
            ((data as any)?.splitPrefill
              ? {
                billName: (data as any).splitPrefill?.billName,
                totalAmount: (data as any).splitPrefill?.totalAmount,
                description: (data as any).splitPrefill?.description,
              }
              : (data as any)?.billName ||
                (data as any)?.totalAmount ||
                (data as any)?.description
                ? {
                  billName: (data as any).billName,
                  totalAmount: (data as any).totalAmount,
                  description: (data as any).description,
                }
                : null);
          dispatch({
            type: "SET_SPLIT_PREFILL_DATA",
            payload: fallbackPrefill
              ? {
                billName: fallbackPrefill.billName,
                totalAmount: fallbackPrefill.totalAmount,
                description: fallbackPrefill.description,
              }
              : null,
          });
        }
              break;
            case "setup-recurring-payment":
              dispatch({
                type: "SET_RECURRING_PAYMENT",
                payload: {
                  id: data.paymentId || null,
                  editMode: data.editMode || false,
                },
              });
              break;
            case "banking-redirect":
              if (data.paymentRequest && data.method) {
                dispatch({ type: "SET_BANKING_REDIRECT", payload: data });
              }
              break;
            case "payment-confirmation":
              if (data.paymentRequest && data.status) {
                dispatch({ type: "SET_PAYMENT_CONFIRMATION", payload: data });
              }
              break;
            case "request":
              if (data.requestData) {
                dispatch({
                  type: "SET_REQUEST_DATA",
                  payload: data.requestData,
                });
              }
              break;
            case "send":
              dispatch({ type: "SET_SEND_DATA", payload: data });
              break;
            case "add-vendor":
              dispatch({
                type: "SET_HISTORY_BACK_TO",
                payload: (data as any)?.from || navState.activeTab || "send",
              });
              break;
        case "orders":
          dispatch({
            type: "SET_HISTORY_BACK_TO",
            payload: (data as any)?.from || navState.activeTab || "profile",
          });
          break;
            case "vendor-shop":
              dispatch({
                type: "SET_VENDOR_SHOP",
                payload: data
                  ? {
                    vendorId: (data as any).vendorId,
                    vendorName: (data as any).vendorName,
                    vendorDescription: (data as any).vendorDescription,
                  }
                  : null,
              });
              break;
            case "send-reminder":
              dispatch({ type: "SET_REMINDER_DATA", payload: data });
              break;
            case "payment-request-cancel":
              if (data.requestId) {
                dispatch({
                  type: "SET_CANCEL_REQUEST_ID",
                  payload: String(data.requestId),
                });
              }
              break;
            case "spending-insights":
              dispatch({
                type: "SET_HISTORY_BACK_TO",
                payload:
                  data && (data as any).from
                    ? (data as any).from
                    : navState.activeTab,
              });
              break;
            case "view-all-friends":
              dispatch({
                type: "SET_FRIEND_PICKER_STATE",
                payload: {
                  selectedIds: Array.isArray((data as any).selectedFriendIds)
                    ? (data as any).selectedFriendIds.map((id: any) =>
                      String(id),
                    )
                    : [],
                  sourceTab: (data as any).from || navState.activeTab || "split",
                },
              });
              dispatch({
                type: "SET_FRIEND_PICKER_SPLIT_PREFILL",
                payload: (data as any)?.splitPrefill || null,
              });
              dispatch({
                type: "SET_FRIEND_PICKER_DELIVERY_OPTION",
                payload: (data as any)?.deliveryOption || null,
              });
              break;
          }
        }
        if (tab === "payment-methods") {
          const backTarget =
            data && (data as any).from
              ? (data as any).from
              : navState.activeTab === "payment-methods"
                ? navState.historyBackTo || "profile"
                : navState.activeTab || "profile";
          dispatch({ type: "SET_HISTORY_BACK_TO", payload: backTarget });
          const startAddFlow = Boolean(
            (data as any)?.startAddFlow,
          );
          dispatch({
            type: "SET_PAYMENT_METHODS_INTENT",
            payload: startAddFlow ? { startAddFlow: true } : null,
          });
        }
        // If navigating to primary tabs without explicit group context, clear lingering group state
        const shouldClearGroupContext =
          (tab === "bills" || tab === "split") &&
          (!data || !data.groupId) &&
          !(tab === "split" && (data as any)?.preserveSplitState);
        if (shouldClearGroupContext) {
          dispatch({ type: "SET_GROUP_ID", payload: null });
          dispatch({ type: "SET_GROUP_CONTEXT", payload: null });
        }
        // Always bump split key when navigating to the Split tab to reset form state
        if (tab === "split") {
          const prefillId =
            data && (data as any).friendId
              ? String((data as any).friendId)
              : null;
          dispatch({ type: "SET_SPLIT_PREFILL_FRIEND_ID", payload: prefillId });
          const selectedFriends = Array.isArray((data as any)?.selectedFriendIds)
            ? (data as any).selectedFriendIds.map((id: any) => String(id))
            : [];
          dispatch({
            type: "SET_PENDING_SPLIT_FRIENDS",
            payload: selectedFriends,
          });
          if ((data as any)?.focusSection) {
            dispatch({
              type: "SET_SPLIT_FOCUS",
              payload: String((data as any).focusSection),
            });
          }
          const preserveSplitState = Boolean(
            (data as any)?.preserveSplitState === true,
          );
          if (!preserveSplitState) {
            dispatch({ type: "BUMP_SPLIT_KEY" });
          }
        }

        dispatch({ type: "SET_TAB", payload: tab });

        // Update hash to reflect current screen for refresh/back support
        const nextHash = encodeHash(tab, data);
        if (window.location.hash !== nextHash) {
          window.location.hash = nextHash;
        }

        performance.mark("navigation-end");
        performance.measure(
          "navigation-duration",
          "navigation-start",
          "navigation-end",
        );
      } catch (error) {
        console.error("Navigation error:", error);
        toast.error("Navigation failed. Please try again.");

        if (tab !== "home") {
          dispatch({ type: "SET_TAB", payload: "home" });
        }
      }
    },
    [
      navState.activeTab,
      navState.paymentRequest,
      navState.paymentRequestRef,
      navState.selectedBillSplitId,
      navState.bankingRedirectData,
      navState.paymentConfirmationData,
      navState.vendorShopData,
    ],
  );

  const clearPendingSplitFriends = useCallback(() => {
    dispatch({ type: "SET_PENDING_SPLIT_FRIENDS", payload: [] });
  }, []);

  const handleNavigateRef = useRef(handleNavigate);

  useEffect(() => {
    handleNavigateRef.current = handleNavigate;
  }, [handleNavigate]);

  // Restore navigation state from URL hash after authentication
  useEffect(() => {
    if (!isAuthenticated) return;
    const { tab, data } = parseHash();
    if (tab) {
      // Use a microtask to ensure reducer is ready
      if (typeof queueMicrotask === "function") {
        queueMicrotask(() => handleNavigateRef.current?.(tab, data));
      } else {
        Promise.resolve().then(() =>
          handleNavigateRef.current?.(tab, data),
        );
      }
    }

    // Listen for back/forward navigation via hash changes
    const onHashChange = () => {
      const { tab: t, data: d } = parseHash();
      if (t) {
        handleNavigateRef.current?.(t, d);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [isAuthenticated]);

  // Prefetch common screens after initial render to make subsequent navigations snappier
  useEffect(() => {
    if (!isAuthenticated) return;
    const w = window as any;
    const queue: Array<() => Promise<unknown>> = [
      () => import("./components/FriendsList"),
      () => import("./components/SplitBill"),
      () => import("./components/BillsScreen"),
      () => import("./components/ProfileScreen"),
      () => import("./components/TransactionHistoryScreen"),
      () => import("./components/SpendingInsightsScreen"),
      () => import("./components/GroupDetailsScreen"),
      () => import("./components/NotificationsScreen"),
      () => import("./components/PaymentMethodsScreen"),
    ];
    let cancelled = false;
    let idleHandle: number | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let startTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (cancelled || queue.length === 0 || !isAuthenticatedRef.current) {
        return;
      }

      const schedule = () => {
        if (cancelled || !isAuthenticatedRef.current) return;
        const loader = queue.shift();
        if (!loader) return;
        loader()
          .catch(() => {
            /* ignore individual prefetch failures */
          })
          .finally(() => scheduleNext());
      };

      if (w.requestIdleCallback) {
        idleHandle = w.requestIdleCallback(
          () => {
            idleHandle = null;
            schedule();
          },
          { timeout: queue.length > 4 ? 1800 : 900 },
        );
      } else {
        timeoutHandle = setTimeout(() => {
          timeoutHandle = null;
          schedule();
        }, queue.length > 4 ? 900 : 450);
      }
    };

    startTimer = setTimeout(() => {
      startTimer = null;
      scheduleNext();
    }, 300);

    return () => {
      cancelled = true;
      if (startTimer !== null) {
        clearTimeout(startTimer);
      }
      if (idleHandle !== null && w.cancelIdleCallback) {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
      }
    };
  }, [isAuthenticated]);

  // Warm up frequently used data so navigation-heavy screens are ready instantly
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let idleHandle: number | null = null;
    let warmupTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let startTimer: ReturnType<typeof setTimeout> | null = null;
    const w = window as typeof window & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions,
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    const warmup = () => {
      if (cancelled || !isAuthenticatedRef.current) return;
      Promise.allSettled([
        import("./hooks/useFriends").then((mod) => mod.fetchFriends?.()),
        import("./utils/split-bill-api").then((mod) => mod.fetchGroups?.()),
        import("./services/paymentMethods").then((mod) =>
          mod.loadPaymentMethods?.(),
        ),
      ]).catch(() => {
        /* ignore background warmup failures */
      });
    };

    const scheduleWarmup = () => {
      if (cancelled || !isAuthenticatedRef.current) return;

      if (w.requestIdleCallback) {
        idleHandle = w.requestIdleCallback(
          () => {
            idleHandle = null;
            warmup();
          },
          { timeout: 1600 },
        );
      } else {
        warmupTimeoutHandle = setTimeout(() => {
          warmupTimeoutHandle = null;
          warmup();
        }, 700);
      }
    };

    startTimer = setTimeout(() => {
      startTimer = null;
      scheduleWarmup();
    }, 320);

    return () => {
      cancelled = true;
      if (startTimer !== null) {
        clearTimeout(startTimer);
      }
      if (idleHandle !== null && w.cancelIdleCallback) {
        w.cancelIdleCallback(idleHandle);
      }
      if (warmupTimeoutHandle) {
        clearTimeout(warmupTimeoutHandle);
      }
    };
  }, [isAuthenticated]);

  // Memoized group navigation handler
  const handleGroupNavigation = useCallback(
    (screen: string, groupId?: string, additionalData?: any) => {
      try {
        const targetGroupId = groupId || navState.currentGroupId;
        if (targetGroupId) {
          handleNavigate(screen, { groupId: targetGroupId, ...additionalData });
        } else {
          console.warn("No group ID available for group navigation");
          toast.error("Group information not available");
        }
      } catch (error) {
        console.error("Group navigation error:", error);
        toast.error("Group navigation failed");
      }
    },
    [navState.currentGroupId, handleNavigate],
  );

  // Auto-resolve legacy onboarding gate for affected users
  useEffect(() => {
    const handler = async (_e: Event) => {
      try {
        // Fetch current user id via auth route (not gated by onboarding)
        const me = await apiClient("/auth/me");
        const id = me?.user?.id;
        if (!id) return;

        // Complete onboarding via allowed endpoint
        await apiClient(`/users/${id}/onboarding`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ onboardingCompleted: true }),
        });

        toast.success("Your account is now ready to use.");
      } catch (err) {
        console.error("Auto-onboarding completion failed:", err);
        toast.error("Please complete onboarding to continue.");
      }
    };

    window.addEventListener("onboarding-required" as any, handler as any);
    return () =>
      window.removeEventListener("onboarding-required" as any, handler as any);
  }, []);

  // Global session-expired handler to avoid crashes after inactivity
  useEffect(() => {
    const onSessionExpired = () => {
      try {
        clearAuth();
        clearBiometricCredential();
        setIsAuthenticated(false);
        setShowLogin(true);
        toast.error("Your session expired. Please log in again.");
        dispatch({ type: "SET_TAB", payload: "login" });
      } catch (e) {
        console.error("session-expired handling error:", e);
      }
    };
    window.addEventListener("session-expired" as any, onSessionExpired as any);
    return () =>
      window.removeEventListener(
        "session-expired" as any,
        onSessionExpired as any,
      );
  }, []);

  // Auto-refresh on resume if session is still valid (no manual reload needed)
  useEffect(() => {
    if (!isAuthenticated) return;

    let lastInteraction = Date.now();

    const touch = () => {
      lastInteraction = Date.now();
    };
    const thresholdMs = 3 * 60 * 1000; // 3 minutes idle threshold

    const softRefresh = () => {
      try {
        window.dispatchEvent(new Event("friendsUpdated"));
        window.dispatchEvent(new Event("notificationsUpdated"));
        window.dispatchEvent(new Event("upcomingPaymentsUpdated"));
        window.dispatchEvent(new Event("groupsUpdated"));
      } catch {
        /* ignore */
      }
    };

    const maybeRefresh = async () => {
      const now = Date.now();
      if (now - lastInteraction < thresholdMs) return;
      try {
        // Ping a lightweight, auth-protected endpoint to validate session
        await apiClient("/notifications/unread");
        softRefresh();
        lastInteraction = now;
      } catch (err) {
        // If unauthorized, apiClient will already dispatch 'session-expired'
        // Do nothing here; the app will route user accordingly.
      }
    };

    window.addEventListener("focus", maybeRefresh);
    const visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        maybeRefresh();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
    window.addEventListener("click", touch);
    window.addEventListener("keydown", touch);
    window.addEventListener("touchstart", touch, { passive: true });

    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", visibilityHandler);
      window.removeEventListener("click", touch);
      window.removeEventListener("keydown", touch);
      window.removeEventListener("touchstart", touch as any);
    };
  }, [isAuthenticated]);

  const handleLogin = useCallback((authResponse?: any) => {
    try {
      setIsInitializing(true);

      const token = authResponse?.token;
      const user = authResponse?.user || {};
      if (!token) {
        throw new Error("No token received");
      }

      const authData = {
        token,
        expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days
        loginTime: new Date().toISOString(),
      };

      saveAuth({ auth: authData, user }, { apiBaseUrl });
      void refreshUserProfile({ waitForStats: false, silent: true });

      setIsAuthenticated(true);
      dispatch({ type: "SET_TAB", payload: "home" });
      toast.success(`Welcome to Bankdrop, ${user.name || "User"}!`);
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.");
    } finally {
      setIsInitializing(false);
    }
  }, [refreshUserProfile]);

  const handleRegister = useCallback(async (userData?: any) => {
    try {
      setIsInitializing(true);

      const data = await apiClient("/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const user = data?.user;
      if (!user || !userData?.phone) {
        throw new Error("Invalid registration response");
      }

      const phone: string = user.phone || String(userData.phone);
      const region = resolveRegionForSignup(userData?.country, phone);

      // Request OTP for phone verification right after signup
      const otpReq = await authService.sendOTP(phone, region);

      // Move to OTP verification screen; do not log in yet
      setPendingOTP({
        phone,
        region,
        isNewUser: true,
        name: user.name,
        demoOTP: otpReq?.otp,
      });

      toast.success("We sent you a verification code to continue");
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Registration failed. Please try again.");
      throw error;
    } finally {
      setIsInitializing(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await apiClient("/auth/logout", { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    }

    try {
      // Clear stored authentication data
      clearAuth();
      clearBiometricCredential();
      localStorage.removeItem("Bankdrop_contacts_synced");

      // Reset app state
      setIsAuthenticated(false);
      setShowLogin(true);
      dispatch({ type: "CLEAR_ALL" });

      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed");
    }
  }, []);

  // Show loading during initialization
  if (isInitializing) {
    return <MemoizedPageLoading />;
  }

  // Show authentication screens if not logged in
  if (!isAuthenticated) {
    if (pendingOTP) {
      const onOtpSuccess = ({ token, user }: { token: string; user: any }) => {
        const authData = {
          token,
          expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000,
          loginTime: new Date().toISOString(),
          isNewUser: pendingOTP.isNewUser,
        } as const;
        saveAuth({ auth: authData, user }, { apiBaseUrl });
        void refreshUserProfile({ waitForStats: false, silent: true });
        setPendingOTP(null);
        setIsAuthenticated(true);
        dispatch({ type: "SET_TAB", payload: "home" });
      };
      return (
        <div className="min-h-screen bg-background">
          <PageErrorBoundary onRetry={() => window.location.reload()}>
            <Suspense fallback={<MemoizedPageLoading />}>
              <OTPVerificationScreen
                phone={pendingOTP.phone}
                region={pendingOTP.region}
                isNewUser={pendingOTP.isNewUser}
                name={pendingOTP.name}
                demoOTP={pendingOTP.demoOTP}
                onSuccess={onOtpSuccess}
                onBack={() => setPendingOTP(null)}
              />
            </Suspense>
          </PageErrorBoundary>
        </div>
      );
    }
    if (showLogin) {
      return (
        <div className="min-h-screen bg-background">
          <PageErrorBoundary onRetry={() => window.location.reload()}>
            <Suspense fallback={<MemoizedPageLoading />}>
              <LoginScreen
                onLogin={handleLogin}
                onShowRegister={(prefill) => {
                  setRegisterPrefill(prefill ?? null);
                  setShowLogin(false);
                }}
                googleEnabled={GOOGLE_AUTH_ENABLED}
              />
            </Suspense>
          </PageErrorBoundary>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background">
        <PageErrorBoundary onRetry={() => window.location.reload()}>
          <Suspense fallback={<MemoizedPageLoading />}>
            <RegisterScreen
              onRegister={handleRegister}
              prefillPhone={registerPrefill?.phone}
              prefillCountry={registerPrefill?.country}
              onShowLogin={() => {
                setShowLogin(true);
                setRegisterPrefill(null);
              }}
              onSocialLogin={handleLogin}
              googleEnabled={GOOGLE_AUTH_ENABLED}
            />
          </Suspense>
        </PageErrorBoundary>
      </div>
    );
  }

  const renderPrimaryTab = (tab: string) => {
    switch (tab) {
      case "home":
        return <HomeScreen onNavigate={handleNavigate} />;
      case "friends":
        return <FriendsList onNavigate={handleNavigate} />;
      case "split":
        console.debug("render: split tab pending friends", {
          pendingFriendIds: navState.pendingSplitFriendIds,
          splitPrefillData: navState.splitPrefillData,
        });
        return (
          <SplitBill
            key={`split-${navState.splitKey}-${navState.currentGroupId || "none"}`}
            onNavigate={handleNavigate}
            groupId={navState.currentGroupId}
            vendorId={navState.vendorId}
            orderDeliveryOption={navState.orderDeliveryOption}
            prefillDetails={navState.splitPrefillData}
            prefillFriendId={navState.splitPrefillFriendId}
            pendingFriendIds={navState.pendingSplitFriendIds}
            onPendingFriendsConsumed={clearPendingSplitFriends}
            focusSection={navState.splitFocusSection}
            onFocusConsumed={() => dispatch({ type: "SET_SPLIT_FOCUS", payload: null })}
            catalogOrder={navState.catalogOrderData}
          />
        );
      case "bills":
        return (
          <BillsScreen
            onNavigate={handleNavigate}
            groupId={navState.currentGroupId}
          />
        );
      case "profile":
        return (
          <ProfileScreen onNavigate={handleNavigate} onLogout={handleLogout} />
        );
      default:
        return <HomeScreen onNavigate={handleNavigate} />;
    }
  };

  const renderContent = () => {
    const isPrimaryTab = PRIMARY_TABS.has(navState.activeTab);
    const requestedPrimary = isPrimaryTab
      ? navState.activeTab
      : lastPrimaryTabRef.current;
    const activePrimaryTab = mountedPrimaryTabs.includes(requestedPrimary)
      ? requestedPrimary
      : initialState.activeTab;

    const renderSecondaryContent = () => {
      switch (navState.activeTab) {
        case "send":
          return (
            <SendMoney
              onNavigate={handleNavigate}
              prefillData={navState.sendMoneyData}
            />
          );
        case "request":
          return (
            <RequestMoney
              onNavigate={handleNavigate}
              prefillData={navState.requestMoneyData}
            />
          );
        case "settings":
          return <SettingsScreen onNavigate={handleNavigate} />;
        case "notifications":
          return <NotificationsScreen onNavigate={handleNavigate} />;
        case "account-settings":
          return <AccountSettingsScreen onNavigate={handleNavigate} />;
        case "business-settings":
          return <BusinessSettingsScreen onNavigate={handleNavigate} />;
        case "payment-methods":
          return (
            <PaymentMethodsScreen
              onNavigate={handleNavigate}
              backTo={navState.historyBackTo || "profile"}
              startAddFlow={Boolean(
                navState.paymentMethodsIntent?.startAddFlow,
              )}
            />
          );
        case "security":
          return <SecurityScreen onNavigate={handleNavigate} />;
        case "kyc-verification":
          return <KycVerificationScreen onNavigate={handleNavigate} />;
        case "transaction-details":
          return (
            <TransactionDetailsScreen
              transactionId={navState.selectedTransactionId}
              onNavigate={handleNavigate}
            />
          );
        case "bill-split-details":
          return (
            <BillSplitDetailsScreen
              billSplitId={navState.selectedBillSplitId}
              onNavigate={handleNavigate}
              catalogOrder={navState.catalogOrderData}
            />
          );
        case "edit-bill-split":
          return (
            <EditBillSplitScreen
              billSplitId={navState.editBillSplitId}
              onNavigate={handleNavigate}
            />
          );
        case "pay-bill":
          return (
            <BillPaymentScreen
              billId={navState.payBillId}
              onNavigate={handleNavigate}
            />
          );
        case "upcoming-payments":
          return <UpcomingPaymentsScreen onNavigate={handleNavigate} />;
        case "payment-flow":
          return (
            <PaymentFlowScreen
              onNavigate={handleNavigate}
              paymentRequest={navState.paymentRequest}
              pendingRequestRef={navState.paymentRequestRef}
              catalogOrder={navState.catalogOrderData}
              backTo={navState.historyBackTo || "home"}
              backPayload={navState.historyBackPayload || undefined}
            />
          );
        case "payment-request-cancel":
          return (
            <PaymentRequestCancelScreen
              onNavigate={handleNavigate}
              requestId={navState.cancelRequestId}
            />
          );
        case "transaction-history":
          return (
            <TransactionHistoryScreen
              onNavigate={handleNavigate}
              backTo={navState.historyBackTo || "home"}
            />
          );
        case "orders":
          return (
            <OrdersScreen
              onNavigate={handleNavigate}
              backTo={navState.historyBackTo || "profile"}
            />
          );
        case "spending-insights":
          return (
            <SpendingInsightsScreen
              onNavigate={handleNavigate}
              backTo={navState.historyBackTo || "home"}
            />
          );
        case "create-group":
          return <CreateGroupScreen onNavigate={handleNavigate} />;
        case "group-details":
          return (
            <GroupDetailsScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
              onGroupNavigation={handleGroupNavigation}
            />
          );
        case "group-account":
          return (
            <GroupAccountScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
            />
          );
        case "group-members":
          return (
            <GroupMembersScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
            />
          );
        case "add-group-member":
          return (
            <AddGroupMemberScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
            />
          );
        case "member-invites":
          return (
            <MemberInviteScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
            />
          );
        case "send-reminder":
          return (
            <SendReminderScreen
              onNavigate={handleNavigate}
              billSplitId={navState.reminderData?.billSplitId}
              paymentType={navState.reminderData?.paymentType}
              friendId={navState.reminderData?.friendId}
              friendName={navState.reminderData?.friendName}
              amount={navState.reminderData?.amount}
            />
          );
        case "friend-profile":
          return (
            <FriendProfileScreen
              friendId={navState.selectedFriendId}
              onNavigate={handleNavigate}
            />
          );
        case "view-all-friends":
          return (
            <ViewAllFriendsScreen
              onNavigate={handleNavigate}
              initialSelectedIds={navState.friendPickerSelection}
              sourceTab={navState.friendPickerSourceTab}
              splitPrefill={navState.friendPickerSplitPrefill}
              deliveryOption={navState.friendPickerDeliveryOption}
            />
          );
        case "add-friend":
          return <AddFriendScreen onNavigate={handleNavigate} />;
        case "add-vendor":
          return (
            <AddVendorScreen
              onNavigate={handleNavigate}
              backTo={navState.historyBackTo || "send"}
            />
          );
        case "contact-sync":
          return <ContactSyncScreen onNavigate={handleNavigate} />;
        case "recurring-payments":
          return <RecurringPaymentsScreen onNavigate={handleNavigate} />;
        case "setup-recurring-payment":
          return (
            <SetupRecurringPaymentScreen
              onNavigate={handleNavigate}
              paymentId={navState.recurringPaymentId}
              editMode={navState.recurringPaymentEditMode}
            />
          );
        case "virtual-account":
          return (
            <VirtualAccountScreen
              groupId={navState.currentGroupId}
              onNavigate={handleNavigate}
            />
          );
        case "banking-redirect":
          return (
            <BankingRedirectScreen
              paymentRequest={
                navState.bankingRedirectData?.paymentRequest || null
              }
              method={navState.bankingRedirectData?.method || null}
              onNavigate={handleNavigate}
            />
          );
        case "payment-confirmation":
          return (
            <PaymentConfirmationScreen
              paymentRequest={
                navState.paymentConfirmationData?.paymentRequest || null
              }
              method={navState.paymentConfirmationData?.method}
              status={navState.paymentConfirmationData?.status || "unknown"}
              onNavigate={handleNavigate}
            />
          );
        case "catalog":
        case "valentine-special":
          return (
            <CatalogScreen
              vendorName={
                userProfile?.businessName?.trim() || userProfile?.name || "Catalog"
              }
              onClose={() => handleNavigate("home")}
              onNavigateToCart={() => handleNavigate("home")}
              onGeneratePaylink={(data) =>
                handleNavigate("request", {
                  requestData: {
                    ...(data?.requestData || data),
                    mode: "pay_link",
                  },
                })
              }
              onSendRequest={(data) =>
                handleNavigate("request", {
                  requestData: {
                    ...(data?.requestData || data),
                    mode: "request",
                  },
                })
              }
            />
          );
        case "vendor-shop":
          return (
            <CatalogScreen
              vendorId={navState.vendorShopData?.vendorId}
              vendorName={navState.vendorShopData?.vendorName || "Vendor shop"}
              vendorDescription={navState.vendorShopData?.vendorDescription}
              onClose={() => handleNavigate("send", { mode: "vendors" })}
              onNavigateToCart={(data) => {
                const prefill =
                  data?.sendMoneyPrefill ||
                  (data
                    ? {
                      recipientId:
                        data.vendorId || navState.vendorShopData?.vendorId,
                      recipientName:
                        data.vendorName || navState.vendorShopData?.vendorName,
                      prefillAmount:
                        typeof data.totalAmount === "number"
                          ? data.totalAmount
                          : Number((data as any)?.requestData?.amount) || undefined,
                      description:
                        data.description ||
                        (data as any)?.requestData?.message ||
                        undefined,
                      mode: "vendors" as const,
                      vendorName:
                        data.vendorName || navState.vendorShopData?.vendorName,
                      deliveryOption: (data as any)?.deliveryOption,
                    }
                    : null);
                handleNavigate("send", prefill || { mode: "vendors" });
              }}
              onGeneratePaylink={(data) => {
                const vendorId =
                  data?.vendorId || navState.vendorShopData?.vendorId || undefined;
                const vendorName =
                  data?.vendorName ||
                  navState.vendorShopData?.vendorName ||
                  "Vendor order";
                const amount =
                  typeof data?.totalAmount === "number"
                    ? data.totalAmount
                    : Number((data as any)?.requestData?.amount) || 0;
                const description =
                  data?.description ||
                  (data as any)?.requestData?.message ||
                  (vendorName ? `Order from ${vendorName}` : "Catalog order");
                const paymentRequest = {
                  id: `vendor-order-${vendorId ?? "guest"}-${Date.now()}`,
                  amount,
                  description,
                  recipient: vendorName,
                  recipientId: vendorId,
                };
                handleNavigate("payment-flow", {
                  paymentRequest,
                  catalogOrder: data?.catalogOrder,
                });
              }}
              onSendRequest={(data) => {
                const splitPrefill = deriveSplitPrefillFromCatalogPayload(data);
                handleNavigate("split", {
                  splitPrefill,
                  vendorId: data?.vendorId || navState.vendorShopData?.vendorId,
                  deliveryOption: (data as any)?.deliveryOption,
                  catalogOrder: data?.catalogOrder,
                  focusSection: "participants",
                  from: "vendor-shop",
                });
              }}
              variant="shopper"
            />
          );
        default:
          console.warn(`Unknown navigation tab: ${navState.activeTab}`);
          return renderPrimaryTab("home");
      }
    };

    try {
      return (
        <>
          <div style={{ display: isPrimaryTab ? "block" : "none" }}>
            {mountedPrimaryTabs.map((tab) => {
              const isActiveTab = tab === activePrimaryTab;
              let content: ReactNode | undefined = tabCacheRef.current.get(tab);

              if (isActiveTab || !content) {
                content = renderPrimaryTab(tab);
                tabCacheRef.current.set(tab, content);
              }

              return (
                <div
                  key={tab}
                  style={{ display: isActiveTab ? "block" : "none" }}
                >
                  {content}
                </div>
              );
            })}
          </div>
          {!isPrimaryTab && renderSecondaryContent()}
        </>
      );
    } catch (error) {
      console.error(
        "Error rendering content for tab:",
        navState.activeTab,
        error,
      );
      throw error;
    }
  };

  // Determine if bottom navigation should be shown
  const showBottomNav = isAuthenticated && PRIMARY_TABS.has(navState.activeTab);

  // Determine if padding should be applied
  const screensThatNeedPadding = new Set([
    "friends",
    "split",
    "bills",
    "profile",
  ]);
  const applyPadding =
    isAuthenticated && screensThatNeedPadding.has(navState.activeTab);

  return (
    <div className="min-h-screen bg-background">
      {/* Network Status Indicator */}
      <NetworkErrorHandler
        showStatus={false}
        autoRetry={false}
        className="fixed top-0 left-0 right-0 z-50"
      />

      {/* Main Content */}
      <main
        className="max-w-md mx-auto bg-background"
        style={{
          minHeight: "100vh",
          paddingBottom: showBottomNav ? "80px" : "0",
        }}
      >
        <div className={applyPadding ? "px-4 py-4" : ""}>
          <ErrorBoundary
            level="page"
            onRetry={() => window.location.reload()}
            onError={(error, errorInfo) => {
              console.error("App-level error:", { error, errorInfo });
            }}
          >
            <Suspense fallback={<MemoizedPageLoading />}>
              <PageErrorBoundary
                onRetry={() => handleNavigate(navState.activeTab)}
              >
                {renderContent()}
              </PageErrorBoundary>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      {/* Bottom Navigation */}
      {showBottomNav && (
        <ErrorBoundary level="component" showHomeButton={false}>
          <BottomNavigation
            activeTab={navState.activeTab}
            onTabChange={handleNavigate}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

const MemoizedAppContent = memo(AppContent);

export default function App() {
  return (
    <CriticalErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_PROVIDER_CLIENT_ID}>
        <ThemeProvider defaultTheme="system">
          <LoadingStateProvider>
            <UserProfileProvider>
              <CopyFeedbackProvider>
                <MemoizedAppContent />
              </CopyFeedbackProvider>
            </UserProfileProvider>
          </LoadingStateProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </CriticalErrorBoundary>
  );
}
